import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import { parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

export enum RiskMatrixOrientation {
  LIKELIHOOD_AS_ROWS = 'likelihood_as_rows',
  LIKELIHOOD_AS_COLUMNS = 'likelihood_as_columns',
}

export enum RiskCalculationMethod {
  DEFAULT = 'default',
  HFS_METHOD = 'hfs_method',
  CONSEQUENCE_MATRIX = 'consequence_matrix_method',
}

export interface ConsequenceMatrixConfig {
  titles: string[]
  rowLabels: number[]
  matrix: string[][]
}

export interface ConsequenceMatrixSelection {
  columnIndex: number
  columnTitle: string
  selectedRowValue: number
  userId: string
  userName: string
  timestamp: number
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- this is the actual type
const isNullOrUndefined = (value: any) => {
  return value === null || value === undefined
}

export interface RiskMatrixLabel {
  value: string
  description: string
}

export interface LikelihoodCriteriaItem {
  description: string
  frequency?: string
  probability?: string
}

/**
 * Default risk colors (text/foreground colors)
 * Used as fallback when no custom risk colors are configured
 */
export const DEFAULT_RISK_COLORS: Record<string, string> = {
  I: '#00BF43', // green
  II: '#FFAE00', // yellow
  III: '#FF6A00', // orange
  IV: '#CD2929', // red
}

/**
 * Default risk background colors
 * Used as fallback when no custom risk background colors are configured
 */
export const DEFAULT_RISK_BACKGROUND_COLORS: Record<string, string> = {
  I: '#0E341B', // green
  II: '#41300E', // yellow
  III: '#41230E', // orange
  IV: '#371616', // red
}

/**
 * Gets the text/foreground color for a given risk level
 * Uses config colors first, then falls back to default colors
 * @param riskLevel - The risk level string (e.g., "I", "II", "III", "IV")
 * @param config - Optional risk assessment config with custom colors
 * @returns The hex color string, or 'white' if not found
 */
export function getRiskColor(
  riskLevel: string,
  config?: GeckoRiskAssessmentConfig
): string {
  // Try config colors first
  const configColor = config?.data.riskColors?.[riskLevel]
  if (configColor) return configColor

  // Fallback to default colors
  return DEFAULT_RISK_COLORS[riskLevel] || 'white'
}

/**
 * Gets the background color for a given risk level
 * Uses config colors first, then falls back to default colors
 * @param riskLevel - The risk level string (e.g., "I", "II", "III", "IV")
 * @param config - Optional risk assessment config with custom colors
 * @returns The hex color string, or '#363636' (gray) if not found
 */
export function getRiskBackgroundColor(
  riskLevel: string,
  config?: GeckoRiskAssessmentConfig
): string {
  // Try config colors first
  const configColor = config?.data.riskBackgroundColors?.[riskLevel]
  if (configColor) return configColor

  // Fallback to default colors
  return DEFAULT_RISK_BACKGROUND_COLORS[riskLevel] || '#363636'
}

/**
 * Default mapping from categorical risk values to 0-100 numeric scores
 * Used as fallback when no custom risk score mapping is configured
 * @param categoryRisk - The categorical risk string (e.g., "I", "II", "III", "IV")
 * @returns The 0-100 risk score
 */
export function mapRiskClassToRiskScoreDefault(categoryRisk: string): number {
  switch (categoryRisk) {
    case 'I':
      return 99
    case 'II':
      return 83
    case 'III':
      return 67
    case 'IV':
      return 30
    default:
      return 0
  }
}

/**
 * Converts a categorical risk value to a 0-100 numeric score
 * Can be used with or without a risk assessment config
 * @param categoryRisk - The categorical risk string (e.g., "I", "II", "III", "IV", etc.)
 * @param config - Optional risk assessment config with custom mappings
 * @returns The 0-100 risk score
 */
export function mapRiskClassToRiskScore(
  categoryRisk: string,
  config?: GeckoRiskAssessmentConfig
): number {
  if (
    config?.data.riskScores &&
    config.data.riskScores[categoryRisk] !== undefined
  ) {
    return config.data.riskScores[categoryRisk]
  }

  // Fallback to default 1-4 scale mapping
  return mapRiskClassToRiskScoreDefault(categoryRisk)
}

/**
 * Default mapping from 0-100 numeric scores to categorical risk values
 * Used as fallback when no custom risk score mapping is configured
 * @param riskScore - The 0-100 risk score
 * @returns The categorical risk string (Roman numerals)
 */
export function mapRiskScoreToRiskClassDefault(riskScore: number): string {
  if (riskScore <= 0 || riskScore > 100) {
    return '0'
  }

  // Default scale mapping (using midpoints: 51, 67, 83, 99)
  if (riskScore <= 30) return 'IV'
  if (riskScore <= 67) return 'III'
  if (riskScore <= 83) return 'II'
  return 'I'
}

/**
 * Converts a 0-100 numeric score to categorical risk format
 * Can be used with or without a risk assessment config
 * @param riskScore - The 0-100 risk score
 * @param config - Optional risk assessment config with custom mappings
 * @returns The categorical risk string
 */
export function mapRiskScoreToRiskClass(
  riskScore: number,
  config?: GeckoRiskAssessmentConfig
): string {
  if (riskScore <= 0 || riskScore > 100) {
    return '0'
  }

  const riskScores = config?.data.riskScores
  if (riskScores && Object.keys(riskScores).length > 0) {
    // Find the category with the lowest score that is >= riskScore
    let bestCategory: string | null = null
    let bestScore = Infinity

    for (const [category, score] of Object.entries(riskScores)) {
      if (score >= riskScore && score < bestScore) {
        bestCategory = category
        bestScore = score
      }
    }

    // If no category found with score >= riskScore, return the highest category
    if (bestCategory === null) {
      bestCategory = Object.keys(riskScores)[0]
      let highestScore = riskScores[bestCategory]

      for (const [category, score] of Object.entries(riskScores)) {
        if (score > highestScore) {
          bestCategory = category
          highestScore = score
        }
      }
    }

    return bestCategory
  }

  // Fallback to default 1-4 scale mapping
  return mapRiskScoreToRiskClassDefault(riskScore)
}

export interface HealthConditionBounds {
  Fair: number
  Bad: number
  VeryPoor: number
}

export const defaultHealthConditionBounds: HealthConditionBounds = {
  Fair: 83,
  Bad: 67,
  VeryPoor: 30,
}

export class GeckoRiskAssessmentConfig extends GeckoEntryBase<
  {
    riskColors: Record<string, string>
    riskMatrix: string[][]
    likelihoodLabels: RiskMatrixLabel[]
    consequenceLabels: RiskMatrixLabel[]
    riskBackgroundColors: Record<string, string>
    riskMatrixOrientation: RiskMatrixOrientation
    riskCalculationMethod: RiskCalculationMethod
    useFormulaForCurrentRisk: boolean
    riskScores: Record<string, number>
    likelihoodCriteria?: LikelihoodCriteriaItem[]
    healthConditionBounds?: HealthConditionBounds
    consequenceMatrix?: ConsequenceMatrixConfig
  },
  {
    site: string
    organization: string
  }
> {
  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        riskColors: isNullOrUndefined(json.data.risk_colors)
          ? {}
          : (json.data.risk_colors as Record<string, string>),
        riskMatrix: isNullOrUndefined(json.data.risk_matrix)
          ? []
          : (json.data.risk_matrix as string[][]),
        likelihoodLabels: isNullOrUndefined(json.data.likelihood_labels)
          ? []
          : (json.data.likelihood_labels as RiskMatrixLabel[]),
        consequenceLabels: isNullOrUndefined(json.data.consequence_labels)
          ? []
          : (json.data.consequence_labels as RiskMatrixLabel[]),
        riskBackgroundColors: isNullOrUndefined(
          json.data.risk_background_colors
        )
          ? {}
          : (json.data.risk_background_colors as Record<string, string>),
        riskMatrixOrientation:
          (parseString(
            json.data.risk_matrix_orientation
          ) as RiskMatrixOrientation) ??
          RiskMatrixOrientation.LIKELIHOOD_AS_ROWS,
        riskCalculationMethod:
          (parseString(
            json.data.risk_calculation_method
          ) as RiskCalculationMethod) ?? RiskCalculationMethod.DEFAULT,
        useFormulaForCurrentRisk: isNullOrUndefined(
          json.data.use_formula_for_current_risk
        )
          ? true
          : (json.data.use_formula_for_current_risk as boolean),
        riskScores: isNullOrUndefined(json.data.risk_scores)
          ? {}
          : (json.data.risk_scores as Record<string, number>),
        likelihoodCriteria: isNullOrUndefined(json.data.likelihood_criteria)
          ? []
          : (json.data.likelihood_criteria as LikelihoodCriteriaItem[]),
        healthConditionBounds: isNullOrUndefined(
          json.data.health_condition_bounds
        )
          ? undefined
          : (json.data.health_condition_bounds as HealthConditionBounds),
        consequenceMatrix: isNullOrUndefined(json.data.consequence_matrix)
          ? undefined
          : (json.data.consequence_matrix as ConsequenceMatrixConfig),
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
      },
      {}
    )
  }

  /**
   * A simple factory method which creates a risk assessment config from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoRiskAssessmentConfig(json)
  }
}
